import useLocalStorage from "use-local-storage";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import Confetti from "react-confetti";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  Box,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  COMPONENT_CUSTOM_MAXHEIGHT,
  CRPS_ORANGE_LIGHT_BRIGHT,
  distinctGrades,
  fetchGetClassroomsAllV,
  fetchGetFailed,
  fetchGetSorted,
  fetchGetStudentsAll,
  fetchPostSort,
  Grade,
  GRADES,
  intensities,
  parseClassroomV,
  parseStudentVFail,
  parseStudentVSorted,
  PSConflictIcon,
  psLocalStorageSorted,
  psLocalStorageSortedTotals,
  schoolGrades,
  SetSnackType,
  SortMetric,
  SettingsGrades,
  SortedWithIntensities,
  STUDENT_INTENSITY_THRESHOLD_HIGH,
  STUDENT_INTENSITY_THRESHOLD_VERYHIGH,
  StudentsNoNulls,
  timeout,
  totalSeats,
  totalSeatsOfGrade,
  VClassroomsNoNulls,
  VFailedNoNulls,
  VSortedNoNulls,
  VStudentsNoNulls,
} from "../../utils";
import { PowerSortButtonGroup } from "../admin";

type SortMetrics = Exclude<keyof typeof SortMetric, "Gender">;
type ClassroomTotals = Record<SortMetrics, number>;
type SortTotals = Record<number, ClassroomTotals>;

const totalsForRows = (sorted: SortedWithIntensities[]): SortTotals => {
  return sorted.reduce<SortTotals>((totals, row) => {
    const classroom = totals[row.c_id] ?? { Achievement: 0, Behaviour: 0, Resource: 0, Social: 0 };
    totals[row.c_id] = {
      Achievement: classroom.Achievement + row.stu_int_ach,
      Behaviour: classroom.Behaviour + row.stu_int_beh,
      Resource: classroom.Resource + row.stu_int_res,
      Social: classroom.Social + row.stu_int_soc,
    };
    return totals;
  }, {});
};

const totalForMetric = (totals: SortTotals | null, metric: SortMetrics) =>
  Object.values(totals ?? {}).reduce(
    (total, classroom) => (typeof classroom === "object" ? total + classroom[metric] : total),
    0,
  );

export const Sorteds = (props: {
  disconnect: () => void;
  conflictClicked: (stuNum: number) => void;
  role: string;
  powerSorting: boolean;
  setPowerSorting: Dispatch<SetStateAction<boolean>>;
  setSnack: SetSnackType;
  settingsGrades: SettingsGrades;
}) => {
  const rowsOrder = (rows: SortedWithIntensities[]): SortedWithIntensities[] =>
    rows.toSorted((a, b) => GRADES.indexOf(a.s_grade) - GRADES.indexOf(b.s_grade) || a.c_name.localeCompare(b.c_name));

  const [storedSelectedRoomId, setStoredSelectedRoomId] = useLocalStorage(psLocalStorageSorted, 0);
  const [storedSortTotals, setStoredSortTotals] = useLocalStorage<SortTotals | null>(psLocalStorageSortedTotals, null);

  const [rows, setRows] = useState(Array<SortedWithIntensities>());
  const [rowsFailed, setRowsFailed] = useState(Array<VFailedNoNulls>());
  const [rowsUnplaced, setRowsUnplaced] = useState(Array<VStudentsNoNulls>());
  const [rowsLoading, setRowsLoading] = useState(false);
  const [classrooms, setClassrooms] = useState(Array<VClassroomsNoNulls>());
  const [selectedClassroom, setSelectedClassroom] = useState<VClassroomsNoNulls | null>(null);
  const [placed, setPlaced] = useState(Array<VStudentsNoNulls>());

  const [gradesCanBeSorted, setGradesCanBeSorted] = useState(Array<Grade>());
  const [totalsBeforeSort, setTotalsBeforeSort] = useState<SortTotals | null>(storedSortTotals);
  const [recycleConfetti, setRecycleConfetti] = useState(false);
  const [runConfetti, setRunConfetti] = useState(false);

  const unselected = !selectedClassroom;

  const failedConflicts = (failed: VFailedNoNulls[]) => failed.filter(f => !f.with_grade);

  const failedUnplaced = (failed: VFailedNoNulls[]) =>
    failed.filter(f => !!f.with_grade).map(f => parseStudentVFail(f));

  const transformSortedWithIntensities = (sorted: VSortedNoNulls[]): SortedWithIntensities[] =>
    sorted.map(i => {
      const stu = parseStudentVSorted(i);
      const { achievement, behaviour, resource, social } = intensities(stu);
      return {
        ...i,
        stu_int_ach: achievement,
        stu_int_beh: behaviour,
        stu_int_res: resource,
        stu_int_soc: social,
      };
    });

  const refresh = async () => {
    setRowsLoading(true);
    try {
      const [resultSorted, resultFailed, resultClassrooms, resultStudents] = await Promise.all([
        fetchGetSorted(),
        fetchGetFailed(),
        fetchGetClassroomsAllV(),
        fetchGetStudentsAll(),
      ]);
      if (!resultSorted || Object.keys(resultSorted).includes("error")) {
        props.disconnect();
        props.setSnack((resultSorted as any).error?.toString(), "error");
        return;
      }
      if (!resultFailed || Object.keys(resultFailed).includes("error")) {
        props.disconnect();
        props.setSnack((resultFailed as any).error?.toString(), "error");
        return;
      }
      if (!resultClassrooms || Object.keys(resultClassrooms).includes("error")) {
        props.disconnect();
        props.setSnack((resultStudents as any).error?.toString(), "error");
        return;
      }
      if (!resultStudents || Object.keys(resultStudents).includes("error")) {
        props.disconnect();
        props.setSnack((resultStudents as any).error?.toString(), "error");
        return;
      }

      const sorted = resultSorted as VSortedNoNulls[];
      const failed = resultFailed as VFailedNoNulls[];
      const classrooms = resultClassrooms as VClassroomsNoNulls[];
      const students = resultStudents as StudentsNoNulls[];

      setRows(rowsOrder(transformSortedWithIntensities(sorted)));

      setRowsFailed(failedConflicts(failed));
      setRowsUnplaced(failedUnplaced(failed));

      setClassrooms(classrooms.filter(c => sorted.map(s => s.c_id).includes(c.id)));

      setGradesCanBeSorted(distinctGrades(students));

      return { sorted, failed };
    } catch (err: any) {
      console.error(err);
    } finally {
      setRowsLoading(false);
    }
  };

  const refRoomId = useRef(new Map<number, HTMLDivElement | null>());

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    const storedSorted = rows.find(r => r.c_id === storedSelectedRoomId);
    if (!storedSorted) return;
    const storedRoom = parseClassroomV(storedSorted);
    if (!storedRoom) return;
    const el = refRoomId.current.get(storedRoom.id);
    if (!el) return;
    el.scrollIntoView({ behavior: "instant", block: "center" });
    setSelectedClassroom(storedRoom);
  }, [rows]);

  useEffect(() => {
    if (!selectedClassroom?.id) return;
    setStoredSelectedRoomId(selectedClassroom.id);
  }, [selectedClassroom]);

  const sort = async (gradesToSort: Grade[]) => {
    setSelectedClassroom(null);
    setRowsFailed([] as VFailedNoNulls[]);
    setRowsUnplaced([] as VStudentsNoNulls[]);
    setTotalsBeforeSort(totalsForRows(rows));
    props.setPowerSorting(true);
    try {
      const result = (await fetchPostSort(gradesToSort)) as any;
      if (Object.keys(result).includes("error")) {
        props.disconnect();
        props.setSnack(result.error?.toString(), "error");
        return;
      }
      props.setPowerSorting(false);

      const { sorted, failed } = (await refresh()) ?? {
        sorted: [] as VSortedNoNulls[],
        failed: [] as VFailedNoNulls[],
      };
      setStoredSortTotals(totalsForRows(transformSortedWithIntensities(sorted)));
      const sortedOfGr = sorted.filter(i => gradesToSort.includes(i.s_grade));
      const stus = sortedOfGr.length;
      const sortedClasses = Array.from(new Set(sortedOfGr.map(i => i.c_id)));
      const rooms = sortedClasses.length;
      const failsOfGr = failed.filter(i => gradesToSort.includes(i.s_grade));
      if (failsOfGr.length > 0) {
        props.setSnack(
          `Sorted ${stus} student${stus === 1 ? "" : "s"} into ${rooms} classroom${
            rooms === 1 ? "" : "s"
          }, but with ${failsOfGr.length} conflict${failsOfGr.length === 1 ? "" : "s"}`,
          "warning",
        );
        return;
      }
      props.setSnack(
        `Sorted ${stus} student${stus === 1 ? "" : "s"} into ${rooms} classroom${rooms === 1 ? "" : "s"}`,
        "success",
      );
      timeout(1).then(() => {
        setRecycleConfetti(true);
        setRunConfetti(true);
        timeout(750)
          .then(() => setRecycleConfetti(false))
          .then(() => timeout(6000).then(() => setRunConfetti(false)));
      });
    } catch (err: any) {
      console.error(err);
      props.setSnack(err, "error");
    }
  };

  useMemo(() => {
    setPlaced(rows.filter(i => i.c_id === selectedClassroom?.id).map(i => parseStudentVSorted(i)));
  }, [rows, selectedClassroom]);

  const TableCellPlaced = ({ children, centre, sx, ...rest }: any) => (
    <TableCell
      sx={{
        textAlign: centre ? "center" : undefined,
        margin: 0,
        padding: 0,
        ...sx,
      }}
      {...rest}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: centre ? "center" : "left",
          justifyContent: centre ? "center" : "left",
        }}
      >
        {children}
      </Box>
    </TableCell>
  );

  const columns = [
    { field: "intensity", label: "Intensity", centre: true, sx: { flex: 1, width: 60, minWidth: 60, maxWidth: 150 } },
    { field: "grade", label: "Grade", centre: true, sx: { flex: 1, width: 70, minWidth: 70, maxWidth: 150 } },
    { field: "last", label: "Last", centre: false, sx: { flex: 1, maxWidth: 250 } },
    { field: "first", label: "First", centre: false, sx: { flex: 1, maxWidth: 250 } },
    { field: "number", label: "ID #", centre: false, sx: { flex: 1, width: 60, minWidth: 60, maxWidth: 150 } },
    { field: "sa_desc", label: "Achievement", centre: true, sx: { flex: 1, width: 50, minWidth: 50, maxWidth: 150 } },
    { field: "sb_desc", label: "Behaviour", centre: true, sx: { flex: 1, width: 100, minWidth: 100, maxWidth: 150 } },
    { field: "ss_desc", label: "Social", centre: true, sx: { flex: 1, width: 106, minWidth: 106, maxWidth: 150 } },
    { field: "ell", label: "ELL", centre: true, sx: { flex: 1, width: 30, minWidth: 30, maxWidth: 150 } },
    { field: "sp_ed_1", label: "SpEd1", centre: true, sx: { flex: 1, width: 55, minWidth: 55, maxWidth: 150 } },
    { field: "sp_ed_2", label: "SpEd2", centre: true, sx: { flex: 1, width: 55, minWidth: 55, maxWidth: 150 } },
    { field: "sp_ed_3", label: "SpEd3", centre: true, sx: { flex: 1, width: 55, minWidth: 55, maxWidth: 150 } },
    { field: "spcl_ed_1", label: "SpClEd1", centre: true, sx: { flex: 1, width: 55, minWidth: 55, maxWidth: 150 } },
    { field: "spcl_ed_2", label: "SpClEd2", centre: true, sx: { flex: 1, width: 55, minWidth: 55, maxWidth: 150 } },
    { field: "conflict", label: "Conflict", centre: true, sx: { flex: 1, width: 50, minWidth: 50, maxWidth: 150 } },
  ];
  const columnsToPrint = columns.filter(c => !["intensity", "conflict"].includes(c.field));

  return (
    <>
      <Box
        className="no-print"
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          height: COMPONENT_CUSTOM_MAXHEIGHT(0),
          maxHeight: COMPONENT_CUSTOM_MAXHEIGHT(0),
          overflowY: "none",
        }}
      >
        <Confetti
          confettiSource={{ x: 250, y: 100, w: 50, h: 50 }}
          initialVelocityY={15}
          gravity={0.7}
          recycle={recycleConfetti}
          run={runConfetti}
          tweenDuration={750}
        />
        {props.powerSorting || rowsLoading ? <LinearProgress /> : <Box sx={{ minHeight: "4px" }}></Box>}
        <Stack
          sx={{
            mt: 0,
            opacity: rowsLoading || props.powerSorting ? 0.3 : 1,
            pointerEvents: rowsLoading || props.powerSorting ? "none" : "auto",
          }}
        >
          <Stack direction={"row"} spacing={5} sx={{ ml: 1, mt: 1 }}>
            <Box sx={{ pl: 1 }}>
              <PowerSortButtonGroup
                {...{
                  disabled: rowsLoading || props.powerSorting,
                  grades: schoolGrades(props.settingsGrades).filter(g => gradesCanBeSorted.includes(g)),
                  role: props.role,
                  sort,
                }}
              />
            </Box>
            <Button
              disabled={rowsLoading || props.powerSorting}
              onClick={ev => window.print()}
              startIcon={<PictureAsPdfIcon />}
              variant="contained"
            >
              Print
            </Button>
          </Stack>
          <Stack direction={"row"} spacing={10} sx={{ width: "100%", justifyContent: "center", my: 1 }}>
            {/* todo: maybe add ` + rowsUnplaced.length` */}
            <Typography component="div">Conflicts: {rowsFailed.length}</Typography>
            <Typography component="div">
              Seating: {rows.length}
              {" / "}
              {classrooms.reduce((acc, el) => (acc += totalSeats(el)), 0)}
            </Typography>
            {(
              [
                ["Total Ach. Int.", SortMetric[SortMetric.Achievement]],
                ["Total Beh. Int.", SortMetric[SortMetric.Behaviour]],
                ["Total Res. Int.", SortMetric[SortMetric.Resource]],
                ["Total Soc. ±", SortMetric[SortMetric.Social]],
              ] as [string, SortMetrics][]
            ).map(([label, metric]) => {
              const previousValue = totalsBeforeSort ? totalForMetric(totalsBeforeSort, metric) : undefined;
              const currentValue = totalForMetric(totalsForRows(rows), metric);
              return (
                <Typography component="div" key={label} sx={{ whiteSpace: "nowrap" }}>
                  {label}:{" "}
                  {!rowsLoading && previousValue !== undefined && previousValue !== currentValue ? (
                    <Tooltip
                      placement="top"
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: "offset",
                              options: { offset: [0, -8] },
                            },
                          ],
                          sx: {
                            "& .MuiTooltip-tooltip": {
                              fontSize: "0.9rem",
                            },
                          },
                        },
                      }}
                      title={"Changes detected since last PS"}
                    >
                      <span>
                        <Box component="span" sx={{ color: "error.main" }}>
                          {previousValue.toLocaleString("en-US")}{" "}
                        </Box>
                        <Box component="span">→ </Box>
                        <Box component="span" sx={{ color: "success.main" }}>
                          {currentValue.toLocaleString("en-US")}
                        </Box>
                      </span>
                    </Tooltip>
                  ) : (
                    <Box component="span">{currentValue.toLocaleString("en-US")}</Box>
                  )}
                </Typography>
              );
            })}
          </Stack>
          <Stack direction={"row"} spacing={1} sx={{ display: "flex" }}>
            <Box
              sx={{
                flex: 1,
                minWidth: 250,
                maxWidth: 350,
                maxHeight: COMPONENT_CUSTOM_MAXHEIGHT(82),
                overflowY: "auto",
              }}
            >
              <Typography component="div" variant="h6" color="text.secondary" sx={{ mx: 2 }}>
                PowerSorted Classrooms
              </Typography>
              <List dense>
                {classrooms
                  .sort(
                    (a, b) =>
                      b.seats0am - a.seats0am ||
                      b.seats0pm - a.seats0pm ||
                      b.seats1 - a.seats1 ||
                      b.seats2 - a.seats2 ||
                      b.seats3 - a.seats3 ||
                      b.seats4 - a.seats4 ||
                      b.seats5 - a.seats5 ||
                      b.seats6 - a.seats6 ||
                      b.seats7 - a.seats7 ||
                      b.seats8 - a.seats8 ||
                      b.seats9 - a.seats9 ||
                      b.seats10 - a.seats10 ||
                      b.seats11 - a.seats11 ||
                      b.seats12 - a.seats12 ||
                      b.seats13 - a.seats13,
                  )
                  .map(room => (
                    <ListItemButton
                      key={room.id}
                      ref={el => {
                        refRoomId.current.set(room.id, el);
                      }}
                      selected={room.id === selectedClassroom?.id}
                      onClick={() => setSelectedClassroom(room)}
                      sx={{ my: 0, py: 0.5 }}
                    >
                      <ListItemText
                        primary={`${room.classroom_name}`}
                        secondary={`${distinctGrades(
                          rows.filter(i => i.c_id === room.id).map(i => parseStudentVSorted(i)),
                        ).join(", ")} - ${(room.prefix ?? "N/A") === "N/A" ? room.first : room.prefix} ${room.last}`}
                        sx={{ my: 0 }}
                      />
                      {rows.filter(i => i.c_id === room.id).some(i => rowsFailed.map(f => f.s_id).includes(i.s_id)) ? (
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                          <PSConflictIcon size={"medium"} disabled />
                        </Box>
                      ) : (
                        <></>
                      )}
                    </ListItemButton>
                  ))}
              </List>
            </Box>
            <Stack
              sx={{
                flex: 3,
                minWidth: 400,
                maxHeight: COMPONENT_CUSTOM_MAXHEIGHT(60),
                overflowY: "none",
              }}
            >
              <Paper sx={{ p: 1 }}>
                <Typography component="div" variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
                  Classroom Details
                </Typography>
                <Stack direction={"row"} spacing={3} sx={{ width: "100%" }}>
                  <Typography component="div">
                    Conflicts:{" "}
                    {unselected
                      ? "—"
                      : rowsFailed.filter(
                          f =>
                            f.sc_owner_desc !== "SYSTEM" &&
                            f.tc_owner_desc !== "SYSTEM" &&
                            placed.find(s => s.id === f.s_id),
                        ).length}
                  </Typography>
                  <Typography component="div">
                    Seating:{" "}
                    {unselected ? (
                      "—"
                    ) : (
                      <>
                        {`${placed.length} / ${totalSeats(selectedClassroom)}`}
                        {distinctGrades(placed).length < 2 ? (
                          <></>
                        ) : (
                          <List>
                            {distinctGrades(placed).map(g => (
                              <ListItem key={`seats-${g}`} sx={{ m: 0, p: 0 }}>{`${g}: ${
                                placed.filter(i => i.grade === g).length
                              } / ${totalSeatsOfGrade(selectedClassroom, g)}`}</ListItem>
                            ))}
                          </List>
                        )}
                      </>
                    )}
                  </Typography>
                  {(
                    [
                      ["Ach. Int.", SortMetric[SortMetric.Achievement]],
                      ["Beh. Int.", SortMetric[SortMetric.Behaviour]],
                      ["Res. Int.", SortMetric[SortMetric.Resource]],
                      ["Soc. ±", SortMetric[SortMetric.Social]],
                    ] as [string, SortMetrics][]
                  ).map(([label, metric]) => {
                    const previousValue = totalsBeforeSort?.[selectedClassroom?.id ?? 0]?.[metric];
                    const currentValue = totalForMetric(
                      totalsForRows(rows.filter(r => r.c_id === selectedClassroom?.id)),
                      metric,
                    );
                    return unselected ? (
                      <Typography component="div" key={label} sx={{ whiteSpace: "nowrap" }}>
                        {label}:{" —"}
                      </Typography>
                    ) : (
                      <Typography component="div" key={label} sx={{ whiteSpace: "nowrap" }}>
                        {label}:{" "}
                        {!rowsLoading && previousValue !== undefined && previousValue !== currentValue ? (
                          <Tooltip
                            placement="top"
                            slotProps={{
                              popper: {
                                modifiers: [
                                  {
                                    name: "offset",
                                    options: { offset: [0, -8] },
                                  },
                                ],
                                sx: {
                                  "& .MuiTooltip-tooltip": {
                                    fontSize: "0.9rem",
                                  },
                                },
                              },
                            }}
                            title={"Changes detected since last PS"}
                          >
                            <span>
                              <Box component="span" sx={{ color: "error.main" }}>
                                {previousValue.toLocaleString("en-US")}{" "}
                              </Box>
                              <Box component="span">→ </Box>
                              <Box component="span" sx={{ color: "success.main" }}>
                                {currentValue.toLocaleString("en-US")}
                              </Box>
                            </span>
                          </Tooltip>
                        ) : (
                          <Box component="span">{currentValue.toLocaleString("en-US")}</Box>
                        )}
                      </Typography>
                    );
                  })}
                </Stack>
              </Paper>
              <Typography component="div" variant="h6" color="text.secondary" sx={{ mt: 2, mb: 0.5 }}>
                Placed Students
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100vh",
                  maxHeight: COMPONENT_CUSTOM_MAXHEIGHT(226),
                  overflowY: "auto",
                }}
              >
                <TableContainer component={Paper} sx={{ flex: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {columns.map((col, i) => (
                          <TableCellPlaced key={`${col.field}-h-${i}`} {...col}>
                            {col.label}
                          </TableCellPlaced>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unselected ? (
                        <TableRow>
                          <TableCell colSpan={columns.length} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No Classroom Selected
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : placed.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={columns.length} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No rows
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        placed
                          .toSorted(
                            (a, b) =>
                              GRADES.indexOf(a.grade) - GRADES.indexOf(b.grade) ||
                              a.last.localeCompare(b.last) ||
                              a.first.localeCompare(b.first),
                          )
                          .map(stu => {
                            const { achievement, resource, social } = intensities(stu);
                            const stuIntensity = achievement + resource + social;
                            return (
                              <TableRow key={stu.id}>
                                {columns.map((col, i) => (
                                  <TableCellPlaced key={`${col.field}-d-${i}`} {...col}>
                                    {col.field === "intensity" ? (
                                      stuIntensity > STUDENT_INTENSITY_THRESHOLD_VERYHIGH ? (
                                        <KeyboardDoubleArrowUpIcon />
                                      ) : stuIntensity > STUDENT_INTENSITY_THRESHOLD_HIGH ? (
                                        <KeyboardArrowUpIcon />
                                      ) : (
                                        <HorizontalRuleIcon />
                                      )
                                    ) : col.field === "conflict" ? (
                                      rowsFailed
                                        .filter(f => f.sc_owner_desc !== "SYSTEM")
                                        .find(f => f.s_id === stu.id) ? (
                                        <PSConflictIcon size={"medium"} onClick={() => props.conflictClicked(stu.id)} />
                                      ) : (
                                        <></>
                                      )
                                    ) : (
                                      stu[col.field as keyof VStudentsNoNulls]
                                    )}
                                  </TableCellPlaced>
                                ))}
                              </TableRow>
                            );
                          })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              {rowsUnplaced.length === 0 ? (
                <></>
              ) : (
                <Box>
                  <Typography component="div" variant="h6" color="text.secondary" sx={{ mt: 2, mb: 0.5 }}>
                    {rowsUnplaced.length} Unplaced Student{rowsUnplaced.length === 1 ? "" : "s"}
                  </Typography>
                  <TableContainer component={Paper} sx={{ minHeight: 100 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {columns.map((col, i) => (
                            <TableCellPlaced key={`${col.field}-h-${i}`} {...col}>
                              {col.label}
                            </TableCellPlaced>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rowsUnplaced.map(unplacedStu => {
                          const { achievement, resource, social } = intensities(unplacedStu);
                          const stuIntensity = achievement + resource + social;
                          return (
                            <TableRow key={unplacedStu.id}>
                              {columns.map((col, i) => (
                                <TableCellPlaced key={`${col.field}-d-${i}`} {...col}>
                                  {col.field === "intensity" ? (
                                    stuIntensity > STUDENT_INTENSITY_THRESHOLD_VERYHIGH ? (
                                      <KeyboardDoubleArrowUpIcon />
                                    ) : stuIntensity > STUDENT_INTENSITY_THRESHOLD_HIGH ? (
                                      <KeyboardArrowUpIcon />
                                    ) : (
                                      <HorizontalRuleIcon />
                                    )
                                  ) : col.field === "conflict" ? (
                                    <PSConflictIcon size={"small"} disabled />
                                  ) : (
                                    unplacedStu[col.field as keyof VStudentsNoNulls]
                                  )}
                                </TableCellPlaced>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Box>
      <div className="sorted-print">
        {Array.from(new Set(rows.map(r => r.c_name))).map((roomName, kRoom) => (
          <div key={kRoom} className="sorted-print-pg">
            <Typography variant="h6" sx={{ color: CRPS_ORANGE_LIGHT_BRIGHT }}>
              {roomName}
            </Typography>
            <TableContainer component={Paper} sx={{ flex: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columnsToPrint.map((col, i) => (
                      <TableCellPlaced key={`${col.field}-h-${i}`} {...col}>
                        {col.label}
                      </TableCellPlaced>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows
                    .filter(r => r.c_name === roomName)
                    .map(iSorted => {
                      const stu = parseStudentVSorted(iSorted);
                      const { achievement, resource, social } = intensities(stu);
                      const stuIntensity = achievement + resource + social;
                      return (
                        <TableRow key={stu.id}>
                          {columnsToPrint.map((col, i) => (
                            <TableCellPlaced key={`${col.field}-d-${i}`} {...col}>
                              {col.field === "intensity" ? (
                                stuIntensity > STUDENT_INTENSITY_THRESHOLD_VERYHIGH ? (
                                  <KeyboardDoubleArrowUpIcon />
                                ) : stuIntensity > STUDENT_INTENSITY_THRESHOLD_HIGH ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <HorizontalRuleIcon />
                                )
                              ) : col.field === "conflict" ? (
                                rowsFailed.filter(f => f.sc_owner_desc !== "SYSTEM").find(f => f.s_id === stu.id) ? (
                                  <PSConflictIcon size={"medium"} onClick={() => props.conflictClicked(stu.id)} />
                                ) : (
                                  <></>
                                )
                              ) : (
                                stu[col.field as keyof VStudentsNoNulls]
                              )}
                            </TableCellPlaced>
                          ))}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        ))}
      </div>
    </>
  );
};
